import { computed, ref, watch } from "vue";
import { eventBus, GlobalEvents } from "../global-events";
import { useCookieStorage, useLocalStorage } from "../use-storage";
import { useWecomaDatalayer } from "./use-wecoma-datalayer";
import { WecomaConstants } from "./wecoma-constants";
import { CookieBarMode, CookieType, WecomaCookie } from "./wecoma-models";
import { ContactWecomaKeys } from "../models/contact";

const cookieBarMode = ref<CookieBarMode>("initial");
const cookieBarModeExpiration = computed(() => {
    switch (cookieBarMode.value) {
        case "allRecheckExpired":
            return WecomaConstants.ALL_RECHECK_EXPIRATION;
        case "someRecheckExpired":
            return WecomaConstants.SOME_RECHECK_EXPIRATION;
        case "initial":
        default:
            return null;
    }
});

const consentsCached = ref<Record<CookieType, boolean> | null>(null);

function initializeConsentsCached() {
    if (consentsCached.value === null) {
        const data = useCookieStorage().getItem(WecomaConstants.CONSENT_CACHE_COOKIE_NAME);

        consentsCached.value = {
            essential: true,
            analytics: data?.[0] === "1",
            marketing: data?.[1] === "1",
            thirdpartycontent: data?.[2] === "1",
        };
    }
}

const getWecomaCookieValue = (value: boolean) => (value ? "1" : "0");

watch(
    consentsCached,
    (newVal) => {
        if (newVal) {
            useCookieStorage().setItem(
                WecomaConstants.CONSENT_CACHE_COOKIE_NAME,
                getWecomaCookieValue(newVal.analytics) +
                    getWecomaCookieValue(newVal.marketing) +
                    getWecomaCookieValue(newVal.thirdpartycontent),
                "essential",
                {
                    expires: WecomaConstants.COOKIE_EXPIRATION,
                    sameSite: "strict",
                }
            );
        }
    },
    { deep: true }
);

const isPlayerLoggedIntoNgWithoutLoadingNgDependencies = () => {
    return new Promise<boolean>((resolve) => {
        if (!window.ngLoader) {
            resolve(false);
            return;
        }

        const isLoggedIn = () => !!ng_fw.getSessionDetails()?.SessionToken;

        if (ngLoader.ngIsReady()) {
            resolve(isLoggedIn());
            return;
        }

        document.addEventListener("fwReady", () => {
            resolve(isLoggedIn());
        });
    });
};

const wecomaLoaded = new Promise<void>((resolve) => {
    if (_gdpr.wecoma) {
        resolve();
        return;
    }
    Wecoma.onready.push(resolve);
}).then(async () => {
    if (useWecomaIntegration().canBeLoggedInWecoma()) {
        const keys = await useWecomaIntegration().getWecomaKeysValue();
        if (keys) {
            const user = await _gdpr.wecoma.registerUser(keys.wecomaUserKey);
            if (!(await _gdpr.wecoma.userHasLocalManagedAuthKey())) {
                await user.registerManagedAuthKey(keys.wecomaDeviceKey);
            }
        }
    }

    const [analyticsConsented, marketingConsented, thirdPartyConsented] = await Promise.all([
        _gdpr.wecoma.isConsented("analytics"),
        _gdpr.wecoma.isConsented("marketing"),
        _gdpr.wecoma.isConsented("thirdpartycontent"),
    ]);

    initializeConsentsCached();

    consentsCached.value.analytics = analyticsConsented;
    consentsCached.value.marketing = marketingConsented;
    consentsCached.value.thirdpartycontent = thirdPartyConsented;
    eventBus.emit(GlobalEvents.ConsentsChanged);
});

export function useWecomaIntegration() {
    let currentCookieValue: WecomaCookie | null = null;

    const {
        pushViewCookieBarInitial,
        pushViewCookieBarRecheckAll,
        pushViewCookieBarRecheckSome,
        pushAllPurposesConsentClick,
        pushOnlyNecessaryPurposesConsent,
        pushRedirectToPreferenceCenter,
        pushReditectToPrivacyPolicy,
        pushThirdPartyConsent,
        pushLinksClickConsentBar,
    } = useWecomaDatalayer(cookieBarMode, cookieBarModeExpiration);

    const canBeLoggedInWecoma = () => !environment.isKasa && environment.siteName != "HrajSRozumem";

    const getWecomaKeysValue = async () => {
        if (await isPlayerLoggedIntoNgWithoutLoadingNgDependencies()) {
            return useLocalStorage().getJson<ContactWecomaKeys>(WecomaConstants.WECOMA_KEY_STORAGE);
        }
        return null;
    };

    const getNowTimestampInSeconds = () => Math.floor(Date.now() / 1_000);

    const createWecomaCookieObject: () => WecomaCookie = () => ({
        createdAt: getNowTimestampInSeconds(),
    });

    const setWecomaCookie = () => {
        useCookieStorage().setItem(
            WecomaConstants.COOKIE_NAME,
            window.btoa(JSON.stringify(createWecomaCookieObject())),
            "essential",
            {
                domain: ".sazka.cz",
                expires: WecomaConstants.COOKIE_EXPIRATION,
            }
        );
    };

    const getWecomaCookieCreateTimestamp = () => {
        if (!currentCookieValue) {
            const val = useCookieStorage().getItem(WecomaConstants.COOKIE_NAME);
            if (val) {
                currentCookieValue = JSON.parse(window.atob(val));
            }
        }

        return currentCookieValue?.createdAt;
    };

    const wecomaCookieExists = () => !!getWecomaCookieCreateTimestamp();

    const deleteWecomaCookie = () => {
        useCookieStorage().removeItem(WecomaConstants.COOKIE_NAME, {
            domain: ".sazka.cz",
        });
    };

    const allPurposesConsented = async () => {
        await wecomaLoaded;
        return await _gdpr.wecoma.allPurposesConsented(_gdpr.wecoma.clientAuthKey, ["consentbar"]);
    };

    const allPurposesRevoked = async () => {
        await wecomaLoaded;
        return await _gdpr.wecoma.allPurposesRevoked(_gdpr.wecoma.clientAuthKey, ["consentbar"]);
    };

    const daysToSeconds = (days: number) => days * 24 * 60 * 60;

    const recheckAllExpired = () =>
        getWecomaCookieCreateTimestamp() <
        getNowTimestampInSeconds() - daysToSeconds(WecomaConstants.ALL_RECHECK_EXPIRATION);

    const recheckSomeExpired = () =>
        getWecomaCookieCreateTimestamp() <
        getNowTimestampInSeconds() - daysToSeconds(WecomaConstants.SOME_RECHECK_EXPIRATION);

    const shouldCookieBarOpen = async () => {
        if (environment.isInAppView) return false;

        await wecomaLoaded;
        let cookieExists = wecomaCookieExists();

        let isLoggedIn = false;
        if (canBeLoggedInWecoma()) {
            const keys = await getWecomaKeysValue();
            if (keys) {
                isLoggedIn = true;
                const anyPurposeHasStatus = await _gdpr.wecoma.anyPurposeHasStatus(_gdpr.wecoma.clientAuthKey, [
                    "consentbar",
                ]);

                if (!anyPurposeHasStatus && cookieExists) {
                    deleteWecomaCookie();
                    cookieExists = false;
                }

                if (anyPurposeHasStatus && !cookieExists) {
                    setWecomaCookie();
                    cookieExists = true;
                }
            }
        }

        if (!cookieExists) {
            pushViewCookieBarInitial(isLoggedIn);
            return true;
        }

        if (await allPurposesConsented()) {
            return false;
        }

        if (await allPurposesRevoked()) {
            if (recheckAllExpired()) {
                cookieBarMode.value = "allRecheckExpired";
                pushViewCookieBarRecheckAll(isLoggedIn);
                return true;
            }
        } else if (recheckSomeExpired()) {
            cookieBarMode.value = "someRecheckExpired";
            pushViewCookieBarRecheckSome(isLoggedIn);
            return true;
        }

        return false;
    };

    const consentToAllPurposes = async (isLoggedIn: boolean) => {
        await wecomaLoaded;
        const purposes = await _gdpr.wecoma.retrievePurposes({
            contexts: ["consentbar"],
            authKeyTypes: [WecomaConstants.WECOMA_AUTHKEY],
        });

        for (const purpose of purposes) {
            purpose.consent();
        }

        pushAllPurposesConsentClick(purposes?.map((p) => p.code)?.join(","), isLoggedIn);

        setWecomaCookie();

        consentsCached.value.analytics = true;
        consentsCached.value.marketing = true;
        consentsCached.value.thirdpartycontent = true;

        eventBus.emit(GlobalEvents.ConsentsChanged);
    };

    const redirectToPreferenceCenter = (variant: "text" | "button", isLoggedIn: boolean) => {
        pushLinksClickConsentBar();
        pushRedirectToPreferenceCenter(variant, isLoggedIn);
    };

    const redirectToPrivacyPolicy = (isLoggedIn: boolean) => {
        pushLinksClickConsentBar();
        pushReditectToPrivacyPolicy(isLoggedIn);
    };

    const cookieTypeConsentedCached = (cookieType: CookieType): boolean => {
        if (cookieType === "essential") return true; // Essential cookies are always consented
        if (!consentsCached.value) return false;
        return consentsCached.value[cookieType];
    };

    const analyticsConsented = async (): Promise<boolean> => {
        await wecomaLoaded;
        return await _gdpr.wecoma.isConsented("analytics");
    };

    const marketingConsented = async (): Promise<boolean> => {
        await wecomaLoaded;
        return await _gdpr.wecoma.isConsented("marketing");
    };

    const thirdPartyConsented = async (): Promise<boolean> => {
        await wecomaLoaded;
        return await _gdpr.wecoma.isConsented("thirdpartycontent");
    };

    const consentToThirdParty = async () => {
        const purpose = await _gdpr.wecoma.retrievePurpose("thirdpartycontent");
        await purpose.consent();
        pushThirdPartyConsent();
        consentsCached.value.thirdpartycontent = true;
        eventBus.emit(GlobalEvents.ConsentsChanged);
    };

    const consentToOnlyNecessaryPurposes = async (isLoggedIn: boolean) => {
        await wecomaLoaded;
        const purposes = await _gdpr.wecoma.retrievePurposes({
            contexts: ["consentbar"],
            authKeyTypes: [WecomaConstants.WECOMA_AUTHKEY],
        });

        for (const purpose of purposes) {
            purpose.revoke();
        }

        pushOnlyNecessaryPurposesConsent(purposes?.map((p) => p.code)?.join(","), isLoggedIn);

        setWecomaCookie();
        consentsCached.value.analytics = false;
        consentsCached.value.marketing = false;
        consentsCached.value.thirdpartycontent = false;
    };

    return {
        canBeLoggedInWecoma,
        getWecomaKeysValue,
        shouldCookieBarOpen,
        consentToAllPurposes,
        redirectToPreferenceCenter,
        redirectToPrivacyPolicy,
        cookieTypeConsentedCached,
        analyticsConsented,
        marketingConsented,
        thirdPartyConsented,
        consentToThirdParty,
        consentToOnlyNecessaryPurposes,
    };
}
